import React, { useState, useEffect } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Typography,
  Divider,
  Timeline,
} from "antd";
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  MailOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useDynamicApi } from "../../hooks/useApi";
import { API_ENDPOINTS } from "../../config/api";
import Toast from "../../utils/toast";
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;

const NotificationDetails = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);

  // API hooks
  const { execute: getNotification, loading: notificationLoading } =
    useDynamicApi("GET");

  // User group options for display
  const USER_GROUP_OPTIONS = [
    { value: "INDIVIDUAL", label: t("notificationManager.groupIndividual") },
    { value: "CLUB_COACH", label: t("notificationManager.groupClubCoach") },
    { value: "CLUB_ADMIN", label: t("notificationManager.groupClubAdmin") },
    {
      value: "KNKV_PARTICIPANT",
      label: t("notificationManager.groupKNKVParticipant"),
    },
    {
      value: "KNKV_INSTRUCTOR",
      label: t("notificationManager.groupKNKVInstructor"),
    },
    { value: "SYSTEM_ADMIN", label: t("notificationManager.groupSystemAdmin") },
  ];

  // Load notification details
  const loadNotificationDetails = async () => {
    setLoading(true);
    try {
      const endpoint = `${API_ENDPOINTS.NOTIFICATION_DETAIL_BY_ID}/${id}`;
      const response = await getNotification(endpoint);

      if (response?.status === true && response.data) {
        setNotification(response.data);
      } else {
        Toast.error(t("notificationManager.failedFetchDetails"));
        navigate("/notification");
      }
    } catch (error) {
      console.error("Error fetching notification details:", error);
      Toast.error(error.message || t("notificationManager.failedFetchDetails"));
      navigate("/notification");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadNotificationDetails();
    }
  }, [id]);

  // Format date
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Get status info
  const getStatusInfo = (status) => {
    switch (status) {
      case "Sent":
        return {
          color: "green",
          icon: <CheckCircleOutlined />,
          label: t("notificationManager.statusSent"),
        };
      case "Failed":
        return {
          color: "red",
          icon: <CloseCircleOutlined />,
          label: t("notificationManager.statusFailed"),
        };
      case "Partial":
        return {
          color: "orange",
          icon: <WarningOutlined />,
          label: t("notificationManager.statusPartial"),
        };
      default:
        return {
          color: "default",
          icon: <CheckCircleOutlined />,
          label: t("notificationManager.statusSent"),
        };
    }
  };

  // Get user group label
  const getUserGroupLabel = (groupValue) => {
    const option = USER_GROUP_OPTIONS.find((opt) => opt.value === groupValue);
    return option ? option.label : groupValue;
  };

  if (loading || notificationLoading) return null;

  if (!notification) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Title level={3}>
            {t("notificationManager.noNotificationsFound")}
          </Title>
          <Button type="primary" onClick={() => navigate("/notification")}>
            {t("common.previous")} {t("notificationManager.notifications")}
          </Button>
        </div>
      </Card>
    );
  }

  const statusInfo = getStatusInfo(notification.status);

  return (
    <div className="notification-details-page">
      {/* Header with back button */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/notification")}
          >
            {t("common.previous")} {t("notificationManager.notifications")}
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {t("notificationManager.notificationDetails")}
          </Title>
        </Space>
      </div>

      {/* Main Content */}
      <Card>
        {/* Header Section */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "24px",
          }}
        >
          <div>
            <h1 style={{ margin: 0, marginBottom: "8px" }}>
              {notification.title}
            </h1>
            <Space size="middle" wrap>
              <Tag color={statusInfo.color} icon={statusInfo.icon}>
                {statusInfo.label}
              </Tag>
              <Text type="secondary">
                <TeamOutlined /> {notification.total_recipients || 0}{" "}
                {t("notificationManager.recipients")}
              </Text>
            </Space>
          </div>
        </div>

        {/* Notification Details */}
        <Descriptions bordered column={2}>
          <Descriptions.Item label={t("notificationManager.title")} span={2}>
            {notification.title}
          </Descriptions.Item>

          <Descriptions.Item label={t("notificationManager.message")} span={2}>
            <div
              style={{
                whiteSpace: "pre-wrap",
                backgroundColor: "#f5f5f5",
                padding: "16px",
                borderRadius: "6px",
                minHeight: "100px",
              }}
            >
              {notification.message}
            </div>
          </Descriptions.Item>

          <Descriptions.Item
            label={t("notificationManager.userGroups")}
            span={2}
          >
            {notification.selected_user_groups?.length > 0 ? (
              <Space wrap>
                {notification.selected_user_groups.map((group) => (
                  <Tag key={group} color="blue">
                    {getUserGroupLabel(group)}
                  </Tag>
                ))}
              </Space>
            ) : (
              <Text type="secondary">{t("notificationManager.noGroups")}</Text>
            )}
          </Descriptions.Item>

          <Descriptions.Item
            label={t("notificationManager.specificUsers")}
            span={2}
          >
            {notification.selected_specific_users?.length > 0 ? (
              <>
                <Text strong>
                  {notification.selected_specific_users.length}
                </Text>{" "}
                {t("notificationManager.usersSelected")}
                <div style={{ marginTop: "8px" }}>
                  <Text type="secondary">
                    {notification.selected_specific_users
                      .slice(0, 5)
                      .map((userId) => {
                        const user = notification.specific_users_details?.find(
                          (u) => u.id === userId,
                        );
                        return user?.full_name || user?.email || userId;
                      })
                      .join(", ")}
                    {notification.selected_specific_users.length > 5 &&
                      ` ${t("common.and")} ${notification.selected_specific_users.length - 5} ${t("common.more")}`}
                  </Text>
                </div>
              </>
            ) : (
              <Text type="secondary">
                {t("notificationManager.noSpecificUsers")}
              </Text>
            )}
          </Descriptions.Item>

          <Descriptions.Item
            label={t("notificationManager.totalRecipients")}
            span={1}
          >
            <Tag color="green">{notification.total_recipients || 0}</Tag>
          </Descriptions.Item>

          <Descriptions.Item label={t("notificationManager.status")} span={1}>
            <Tag color={statusInfo.color} icon={statusInfo.icon}>
              {statusInfo.label}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label={t("notificationManager.sentBy")} span={1}>
            <Space>
              <UserOutlined />
              {notification.sent_by?.full_name ||
                notification.sent_by?.email ||
                t("notificationManager.system")}
            </Space>
          </Descriptions.Item>

          <Descriptions.Item label={t("notificationManager.sentAt")} span={1}>
            <Space>
              <CalendarOutlined />
              {formatDateTime(notification.created_at)}
            </Space>
          </Descriptions.Item>

          {notification.scheduled_for && (
            <Descriptions.Item
              label={t("notificationManager.scheduledFor")}
              span={2}
            >
              <Space>
                <ClockCircleOutlined />
                {formatDateTime(notification.scheduled_for)}
              </Space>
            </Descriptions.Item>
          )}

          {notification.error_message && (
            <Descriptions.Item
              label={t("notificationManager.errorMessage")}
              span={2}
            >
              <Text type="danger">{notification.error_message}</Text>
            </Descriptions.Item>
          )}

          {notification.read_count !== undefined && (
            <Descriptions.Item
              label={t("notificationManager.readCount")}
              span={1}
            >
              {notification.read_count} / {notification.total_recipients}
            </Descriptions.Item>
          )}
        </Descriptions>

        {/* Timeline Section */}
        <Divider orientation="left" style={{ margin: "24px 0 16px" }}>
          {t("notificationManager.notificationTimeline")}
        </Divider>

        <Timeline mode="left" style={{ marginTop: "16px" }}>
          <Timeline.Item
            label={formatDateTime(notification.created_at)}
            color="green"
            dot={<CheckCircleOutlined />}
          >
            <Text strong>{t("notificationManager.notificationCreated")}</Text>
            <br />
            <Text type="secondary">
              {t("notificationManager.notificationInitiated")}
            </Text>
          </Timeline.Item>

          <Timeline.Item
            label={formatDateTime(notification.updated_at)}
            color={statusInfo.color}
            dot={statusInfo.icon}
          >
            <Text strong>
              {t("notificationManager.notificationStatus")}: {statusInfo.label}
            </Text>
            <br />
            <Text type="secondary">
              {notification.status === "Sent"
                ? t("notificationManager.sentSuccessfully")
                : notification.status === "Failed"
                  ? t("notificationManager.failedToSend")
                  : t("notificationManager.partiallySent")}
            </Text>
          </Timeline.Item>

          {notification.delivered_at && (
            <Timeline.Item
              label={formatDateTime(notification.delivered_at)}
              color="blue"
              dot={<MailOutlined />}
            >
              <Text strong>
                {t("notificationManager.deliveredToRecipients")}
              </Text>
            </Timeline.Item>
          )}
        </Timeline>
      </Card>
    </div>
  );
};

export default NotificationDetails;
